import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Guides } from '@/lib/api/v1';
import { Badge, Card, EmptyState, Icon, Skeleton } from '@/design-system';
import { GuideCover, guideCategoryLabel, guideDifficultyLabel, guideTimeLabel } from '@/components/GuideCover';

/**
 * Guide detail — title, meta, ingredients/tools, ordered steps, and a
 * "now you can make these" cocktail-cross-link footer.
 */
export function GuideDetailPage() {
  const { slug = '' } = useParams();

  const guideQ = useQuery({
    queryKey: ['guide', slug],
    queryFn: () => Guides.get(slug),
    enabled: Boolean(slug),
    staleTime: 10 * 60_000,
  });

  if (guideQ.isLoading) {
    return (
      <div className="grid gap-4">
        <Skeleton height={28} width="200px" />
        <Skeleton height={120} />
        <Skeleton height={200} />
      </div>
    );
  }

  if (guideQ.isError || !guideQ.data) {
    return (
      <Card className="p-6">
        <EmptyState icon="warning" title="Guiden ble ikke funnet." />
        <Link to="/guides" className="mt-4 inline-block text-sm text-candle hover:underline">
          ← Tilbake til guides
        </Link>
      </Card>
    );
  }

  const g = guideQ.data;
  const time = guideTimeLabel(g.totalMinutes, true);
  const difficulty = guideDifficultyLabel(g.difficulty);

  return (
    <div className="grid max-w-5xl gap-6">
      <div>
        <Link
          to="/guides"
          className="inline-flex items-center gap-2 text-sm text-mist hover:text-candle"
        >
          <Icon name="chevron-left" size={14} /> Tilbake til guides
        </Link>
      </div>

      <GuideCover category={g.category} variant="hero" />

      <header className="grid max-w-3xl gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="mist">{guideCategoryLabel(g.category)}</Badge>
          <Badge tone="gold">{time}</Badge>
          <Badge tone="mist">{difficulty}</Badge>
          {g.tags.map((t) => (
            <Badge key={t} tone="mist">{t}</Badge>
          ))}
        </div>
        <h1 className="font-serif text-4xl text-parchment">{g.title}</h1>
        <p className="max-w-2xl text-base leading-relaxed text-parchment-dim">{g.description}</p>
        {(g.yields || g.keepsFor) && (
          <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-mist">
            {g.yields && (
              <span className="flex items-center gap-1">
                <Icon name="container" size={10} /> Mengde: <span className="text-parchment">{g.yields}</span>
              </span>
            )}
            {g.keepsFor && (
              <span className="flex items-center gap-1">
                <Icon name="clock" size={10} /> Holdbarhet: <span className="text-parchment">{g.keepsFor}</span>
              </span>
            )}
          </div>
        )}
      </header>

      <Card className="grid gap-3 p-5">
        <div className="flex items-center gap-2">
          <Icon name="info" className="text-candle" size={16} />
          <h2 className="font-serif text-xl text-parchment">Før du starter</h2>
        </div>
        <p className="max-w-3xl text-sm leading-relaxed text-mist">
          Bruk guiden som en arbeidsflyt, ikke en rigid oppskrift. Mål opp alt først, jobb rent, og smak underveis
          når guiden handler om sirup, pynt eller lavalkohol. Små justeringer i sødme, fortynning og temperatur
          gjør ofte større forskjell enn ekstra utstyr.
        </p>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[minmax(220px,0.8fr)_minmax(0,2fr)]">
        {/* Tools + ingredients */}
        <div className="grid gap-4">
          {g.tools.length > 0 && (
            <Card className="grid gap-2 p-4">
              <div className="flex items-center gap-2">
                <Icon name="settings" className="text-candle" size={14} />
                <h2 className="font-serif text-sm text-parchment">Du trenger</h2>
              </div>
              <ul className="grid gap-1 text-sm">
                {g.tools.map((tool) => (
                  <li key={tool} className="flex items-baseline gap-2 text-parchment">
                    <Icon name="check" size={10} className="text-mist" />
                    {tool}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {g.ingredients.length > 0 && (
            <Card className="grid gap-2 p-4">
              <div className="flex items-center gap-2">
                <Icon name="list" className="text-candle" size={14} />
                <h2 className="font-serif text-sm text-parchment">Ingredienser</h2>
              </div>
              <ul className="grid gap-2">
                {g.ingredients.map((ing, i) => (
                  <li key={i} className="grid gap-0.5 border-b border-line pb-1.5 last:border-b-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm text-parchment">{ing.name}</span>
                      {ing.amount && (
                        <span className="text-xs text-mist tabular-nums">{ing.amount}</span>
                      )}
                    </div>
                    {ing.notes && (
                      <span className="text-[10px] text-mist italic">{ing.notes}</span>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        {/* Steps */}
        <Card className="grid gap-4 p-5">
          <div className="flex items-center gap-2">
            <Icon name="chef-hat" className="text-candle" size={16} />
            <h2 className="font-serif text-2xl text-parchment">Slik gjør du det</h2>
          </div>
          <ol className="grid gap-5">
            {g.steps.map((step, idx) => (
              <li key={idx} className="grid gap-2 border-b border-line pb-5 last:border-b-0 last:pb-0">
                <div className="flex items-baseline gap-3">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-candle/10 text-sm font-medium text-candle">
                    {idx + 1}
                  </span>
                  {step.title ? (
                    <span className="font-serif text-xl text-parchment">{step.title}</span>
                  ) : null}
                </div>
                <p className="pl-10 text-base leading-relaxed text-parchment-dim">
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </Card>
      </div>

      {g.unlocks.length > 0 && (
        <Card className="grid gap-3 p-5">
          <div className="flex items-center gap-2">
            <Icon name="sparkles" className="text-candle" size={16} />
            <h2 className="font-serif text-lg text-parchment">Du kan nå lage</h2>
          </div>
          <p className="text-xs text-mist">Cocktails som krever det du nettopp lærte:</p>
          <div className="flex flex-wrap gap-2">
            {g.unlocks.map((slug) => (
              <Link
                key={slug}
                to={`/bar/${slug}`}
                className="ui-pill"
              >
                <Icon name="bottle" size={12} />
                {slug}
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
